/**
 * The invitation token — the widget's only on-switch.
 *
 * A review link goes to /r/<token> on the Spotnote domain, which redirects to
 * the customer's site with `?feedback=<token>` attached. The widget stores the
 * token in localStorage of the origin it runs on (the only storage it can both
 * write and read across that site's pages), so feedback mode survives
 * navigation after the query parameter is gone.
 *
 * The stored value is a HINT, not a permission: the loader revalidates it
 * against the API on every page load, and the server's answer is what decides
 * whether the widget exists. A round ended on the server means the flag gets
 * cleared here on the next load — client state can never keep a dead round's
 * widget alive.
 */

const KEY = 'spotnote_t'
const QUERY_PARAM = 'feedback'

/** How long a stored token is worth revalidating before we drop it locally. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

interface Stored {
  t: string
  /** Epoch milliseconds. */
  exp: number
}

/**
 * localStorage throws rather than returning null when a browser refuses it:
 * Safari in private mode, third-party contexts with storage partitioned off,
 * or a user who disabled site data. None of that should stop feedback being
 * collected — it only means the token cannot outlive the query parameter.
 */
function storage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/**
 * The token for this visitor, if any.
 *
 * A token in the URL always wins and is written through to storage: it is the
 * freshest statement of which round this person is feedbacking into. Someone
 * who is in two rounds follows whichever link they clicked last — deliberate,
 * and far simpler than a round switcher in the widget.
 */
export function readReviewToken(): string | undefined {
  const store = storage()

  const fromQuery = new URLSearchParams(window.location.search).get(QUERY_PARAM)
  if (fromQuery) {
    try {
      store?.setItem(KEY, JSON.stringify({ t: fromQuery, exp: Date.now() + MAX_AGE_MS } satisfies Stored))
    } catch {
      // Quota or a private-mode write rejection. The token still works for
      // this page load.
    }
    return fromQuery
  }

  const raw = store?.getItem(KEY)
  if (raw) {
    try {
      const stored = JSON.parse(raw) as Stored
      if (stored.t && stored.exp > Date.now()) return stored.t
      store?.removeItem(KEY)
    } catch {
      // Somebody else's key, or a value we wrote in an older format.
      store?.removeItem(KEY)
    }
  }

  return undefined
}

/**
 * Drops the stored token. Called when the server says the round is over or the
 * invitation was revoked — the hint is stale and keeping it would cost a
 * pointless API call on every future page load.
 */
export function forgetReviewToken(): void {
  try {
    storage()?.removeItem(KEY)
  } catch {
    // Nothing to forget if storage is unavailable.
  }
}
